import { selMessage } from "@selector/shared";
import { Component, type ReactElement, type ReactNode } from "react";
import { InfoToggletip, SymbolIcon } from "widgets";

import {
  controlledVocOptionValidation,
  dateValidation,
  dynamicTermOptionValidation,
  filePickerValidation,
  freeTextValidation,
  genomicRegionValidation,
  hyperlinkValidation,
  numberValidation,
  refGenomeValidation,
  whiteSpaceValidation,
} from "@/app-utils/validation.ts";
import { type Attribute, type Organism, type ProjectFile } from "@/graphql.ts";
import { getGenomeWorkflowParams } from "@/redux/modules/playbook-execution.ts";
import { getAttributeLabel } from "@/redux/modules/sample-admin.ts";
import { ATTRIBUTE_TYPE, FILE_BROWSE_TYPES, MAX_FREE_TEXT_LENGTH, REPRESENTER_COLUMN } from "@/settings/settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

import { type ISelectOption } from "../../../app-utils/form.js";
import { type IConfiguredAttribute } from "../../../app-utils/playbook.js";
import { getUniqueAttributeId } from "../../../app-utils/sample.js";
import { FieldTitle } from "../../playbooks/FieldTitle.js";
import CheckboxField, { CheckboxReduxFormField } from "../redux-form/CheckboxField.js";
import { FilePickerReduxFormField } from "../redux-form/FilePickerFormField.js";
import InputField, { InputReduxFormField } from "../redux-form/InputField.js";
import { ReactDateSelectReduxFormField } from "../redux-form/ReactDateSelectorField.js";
import { RefGenomeReduxFormField } from "../redux-form/ReactRefGenomeField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../redux-form/ReactSelectField.js";
import { RegionSelectorReduxFormField } from "../redux-form/RegionSelectorField.js";

export interface IAttributeInputFieldProps {
  attribute: IConfiguredAttribute;
  noFileSelectionText?: string;
  validate?: (value: any, allValues: any, props: any) => string;
  checkAttributeUniquenessOnClient?: (attribute: Attribute, value: string) => string;
  verticalLayout?: boolean;
  selectedGenome?: Organism; // useful for specific attribute types that depend on the selected genome
  onlyExplorerGenome?: boolean; // meant for genome input field
  acceptExplorerSequences?: boolean; // meant for genome input field
  disabledByEditOnce?: boolean; // if edit_once and here is already a value, we need to disable the field
}

class AttributeInputField extends Component<IAttributeInputFieldProps> {
  constructor(props: IAttributeInputFieldProps) {
    super(props);
  }

  buildLabel = () => {
    const attribute: IConfiguredAttribute = this.props.attribute;
    return getAttributeLabel({
      ...attribute,
      attributeName: attribute.label || attribute.attributeName,
    });
  };

  buildTitle = () => {
    const attribute: IConfiguredAttribute = this.props.attribute;
    let labelIcon: ReactNode;
    let title: string;

    if (attribute.automatic) {
      title = "This value is automatically populated";
      labelIcon = <SymbolIcon icon={"lock"} size={12} />;
    } else if (!attribute.modifiable) {
      title = "This value cannot be modified";
      labelIcon = <SymbolIcon icon={"lock"} size={12} />;
    } else if (attribute.mandatory || attribute.correctedMandatory) {
      title = selMessage({ id: "form.input.tooltip.mandatory" });
      labelIcon = "*";
    }
    const hintIcon = attribute.description && <InfoToggletip content={attribute.description} />;
    return <FieldTitle label={this.buildLabel()} title={title} labelIcon={labelIcon} rightElement={hintIcon} />;
  };

  buildTooltip = () => {
    const attribute: IConfiguredAttribute = this.props.attribute;

    return attribute.automatic
      ? "This value is automatically generated and cannot be modified"
      : !attribute.modifiable
        ? "This value cannot be modified"
        : attribute.mandatory || attribute.correctedMandatory
          ? null
          : null;
  };

  buildPlaceholder = () => {
    const attribute: IConfiguredAttribute = this.props.attribute;

    return (
      <FormInputGroup key={`parent-attribute-${attribute.id}`} horizontalLayout>
        <FormLabel>{this.buildLabel()}</FormLabel>
        <FormInputWidget>
          Attribute type <strong>{attribute.attributeType.name}</strong> is not yet supported
        </FormInputWidget>
      </FormInputGroup>
    );
  };

  // in order to NOT create inline validation method in the input field (which would trigger a refresh and therefore unregister/register in redux-form since v7.0)
  // (see https://github.com/erikras/redux-form/issues/4017)
  freeTextValidation = (value: string) => {
    const uniquenessCheckResult: string =
      this.props.checkAttributeUniquenessOnClient &&
      this.props.attribute.valueUniqueConstraint &&
      this.props.checkAttributeUniquenessOnClient(this.props.attribute, value);
    const textValidationResult: string = freeTextValidation(value, this.props.attribute);
    const whiteSpaceValidationResult: string = whiteSpaceValidation(value);
    const notModifiableButEmptyNameRepresenterResult: string | null =
      this.props.attribute.representedColumn === REPRESENTER_COLUMN.NAME && !this.props.attribute.modifiable && !value
        ? "This value is required, however the attribute is not modifiable. The sample type configuration is incorrect."
        : null;
    return (
      textValidationResult ||
      uniquenessCheckResult ||
      whiteSpaceValidationResult ||
      notModifiableButEmptyNameRepresenterResult
    );
  };

  controlledVocValidation = (value: ISelectOption) => controlledVocOptionValidation(value, this.props.attribute);
  numberValidation = (value: number) => numberValidation(value, this.props.attribute);
  dateValidation = (value) => dateValidation(value, this.props.attribute);
  hyperlinkValidation = (value) => hyperlinkValidation(value, this.props.attribute);
  dynamicTermsValidation = (value: ISelectOption) => dynamicTermOptionValidation(value, this.props.attribute);
  filePickerValidation = (value: ProjectFile[]) => filePickerValidation(value, this.props.attribute);
  refGenomeValidation = (value) => refGenomeValidation(value, this.props.attribute);
  genomicRegionValidation = (value) => genomicRegionValidation(value, this.props.attribute);

  render(): ReactElement {
    const attribute: IConfiguredAttribute = this.props.attribute;

    const attributeTypeId: number = attribute.correctedTypeId || attribute.attributeType.id;
    const disabledAttribute: boolean = attribute.automatic || !attribute.modifiable || !!this.props.disabledByEditOnce;
    const uniqueIdentifier: string = getUniqueAttributeId(attribute);
    const key: string = attribute.id;
    const title: any = this.buildTitle();
    const tooltip: string = this.buildTooltip();

    switch (attributeTypeId) {
      case ATTRIBUTE_TYPE.FREE_TEXT:
        // eslint-disable-next-line no-case-declarations
        const maxLength = Math.min(
          attribute.representedColumn === "name" ? 150 : MAX_FREE_TEXT_LENGTH,
          attribute.maxLength != null ? attribute.maxLength : MAX_FREE_TEXT_LENGTH,
        );

        return (
          <InputReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            label={title}
            componentClass="input"
            component={InputField}
            placeholder="Enter text..."
            maxLength={maxLength}
            disabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.props.validate || this.freeTextValidation}
          />
        );
      case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
        // eslint-disable-next-line no-case-declarations
        const terms = attribute.vocabularyTerms.slice(0);
        // eslint-disable-next-line no-case-declarations
        const options = terms
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((x) => ({
            label: x.name,
            value: x.id,
          }));
        return (
          <ReactSelectReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            isClearable={false}
            isSearchable={true}
            label={title}
            placeholder={"Please select..."}
            component={ReactSelectField}
            options={options}
            isDisabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.props.validate || this.controlledVocValidation}
          />
        );

      case ATTRIBUTE_TYPE.FREE_NUMERICAL:
      case ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL:
        return (
          <InputReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            label={title}
            componentClass="input"
            component={InputField}
            type="number"
            placeholder="Enter number..."
            disabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.props.validate || this.numberValidation}
          />
        );

      case ATTRIBUTE_TYPE.CALCULATED:
        return this.buildPlaceholder();

      case ATTRIBUTE_TYPE.DATE:
        return (
          <ReactDateSelectReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            title={title}
            withTime={false}
            placeholder="Select a date..."
            tooltip={tooltip}
            disabled={disabledAttribute}
            validate={this.props.validate || this.dateValidation}
          />
        );

      case ATTRIBUTE_TYPE.HYPERLINK:
        return (
          <InputReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            label={title}
            componentClass="textarea"
            component={InputField}
            placeholder="Enter Hyperlink..."
            maxLength={attribute.maxLength || MAX_FREE_TEXT_LENGTH}
            disabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.props.validate || this.hyperlinkValidation}
          />
        );

      case ATTRIBUTE_TYPE.BOOLEAN:
        return (
          <CheckboxReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            leftLabel={title}
            label={this.props.verticalLayout ? title : null}
            disabled={disabledAttribute}
            component={CheckboxField}
          />
        );

      case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
        // eslint-disable-next-line no-case-declarations
        const dynamicTermsOptions: { label: string; value: number }[] = !attribute.dynamicTerms
          ? []
          : attribute.dynamicTerms.map((x) => ({
              label: x.name,
              value: x.id,
            }));

        return (
          <ReactSelectReduxFormField
            horizontalLayout={!this.props.verticalLayout}
            key={key}
            name={uniqueIdentifier}
            isClearable={false}
            isSearchable={true}
            label={title}
            placeholder={"Please select..."}
            component={ReactSelectField}
            options={dynamicTermsOptions}
            validate={this.dynamicTermsValidation}
            virtualizedSelectWrapper={true}
            isDisabled={disabledAttribute}
            tooltip={tooltip}
          />
        );
      case ATTRIBUTE_TYPE.FILE_OR_FOLDER:
      case ATTRIBUTE_TYPE.FILE:
      case ATTRIBUTE_TYPE.FOLDER: {
        let selectableFileTypes = undefined;
        if (attributeTypeId === ATTRIBUTE_TYPE.FILE) {
          selectableFileTypes = [FILE_BROWSE_TYPES.REGULAR_FILE];
        } else if (attributeTypeId === ATTRIBUTE_TYPE.FOLDER) {
          selectableFileTypes = [FILE_BROWSE_TYPES.DIRECTORY];
        }
        return (
          <FilePickerReduxFormField
            key={key}
            horizontalLayout={!this.props.verticalLayout}
            name={uniqueIdentifier}
            title={title}
            isDisabled={disabledAttribute}
            tooltip={tooltip}
            singleSelection={!attribute.multipleValues} // only used for file picker
            selectableFileTypes={selectableFileTypes}
            validate={this.props.validate || this.filePickerValidation}
            noSelectionText={this.props.noFileSelectionText}
          />
        );
      }
      case ATTRIBUTE_TYPE.GENOME_SELECTION:
        return (
          <RefGenomeReduxFormField
            key={key}
            horizontalLayout={!this.props.verticalLayout}
            name={uniqueIdentifier}
            title={title}
            isDisabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.props.validate || this.refGenomeValidation}
            onlyExplorerGenome={this.props.onlyExplorerGenome}
          />
        );
      case ATTRIBUTE_TYPE.GENOMIC_COORDINATES:
        return (
          <RegionSelectorReduxFormField
            name={uniqueIdentifier}
            title={title}
            genomeId={this.props.selectedGenome?.id}
            horizontalLayout={!this.props.verticalLayout}
            isDisabled={disabledAttribute}
            tooltip={tooltip}
            validate={this.genomicRegionValidation}
          />
        );
      default:
        throw new Error("Unknown attribute type of id " + attributeTypeId);
    }
  }
}

export default AttributeInputField;

export function getSelectedOrganismFromForm(experimentAttributes: Attribute[], formValues: any): Organism {
  const workflowGenomeAttributes: Attribute[] = getGenomeWorkflowParams(experimentAttributes);
  // if there is no genome input, no explorer genome can be selected
  if (workflowGenomeAttributes.length === 0 || !formValues) {
    return null;
  }
  let selectedExplorerGenome: Organism = null;
  // otherwise check redux form state if an object (= Explorer Genome) is selected
  workflowGenomeAttributes.forEach((a: Attribute) => {
    const uniqueIdentifier: string = getUniqueAttributeId(a);
    const selectedGenome: any = formValues[uniqueIdentifier];
    if (selectedGenome && !Array.isArray(selectedGenome)) {
      selectedExplorerGenome = selectedGenome as Organism;
    }
  });
  return selectedExplorerGenome;
}
